import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface } from "@/components/common";
import { LanguageSelect } from "@/components/LanguageSelect";
import { Button } from "@/components/ui/button";
import { RefreshCw, PenLine, Linkedin, Mail, Newspaper, Megaphone, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const MODULE_MAP = {
  linkedin: { route: "/linkedin", icon: Linkedin, label: "LinkedIn" },
  newsletter: { route: "/nyhedsbrev", icon: Mail, label: "Nyhedsbrev" },
  press: { route: "/presse", icon: Newspaper, label: "Presse" },
  campaign: { route: "/kampagner", icon: Megaphone, label: "Kampagne" },
  idea: { route: "/idebank", icon: Lightbulb, label: "Idé" },
};

function greeting() {
  const h = new Date().getHours();
  if (h < 10) return "Godmorgen";
  if (h < 18) return "Goddag";
  return "Godaften";
}

export default function Weekly() {
  const { user, company } = useAuth();
  const { t, contentLang } = useLocale();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [language, setLanguage] = useState(contentLang);

  const load = async (lang = language) => {
    try {
      const { data } = await api.get("/dashboard/suggestions", { params: { language: lang } });
      setSuggestions(data.suggestions);
    } catch (err) {
      toast.error(apiError(err));
      setSuggestions([]);
    }
  };

  useEffect(() => { load(); }, []);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    setSuggestions(null);
    load(lang);
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/dashboard/refresh", { language });
      setSuggestions(data.suggestions);
      toast.success(t("Nye forslag klar"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRefreshing(false);
    }
  };

  const draft = (s) => {
    const m = MODULE_MAP[s.module] || MODULE_MAP.idea;
    navigate(m.route, { state: { topic: s.description || s.title } });
  };

  return (
    <div>
      <PageHeader
        overline="Ugens forslag"
        title={`${t(greeting())}, ${(user?.name || "").trim().split(" ")[0] || t("der")} 👋`}
        subtitle="Her er, hvad Aicom AI anbefaler, at I kommunikerer om denne uge."
      />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display text-xl font-medium">{t("Dine kommunikationsforslag denne uge")}</h2>
        <div className="flex flex-wrap items-center gap-3">
          <LanguageSelect value={language} onChange={changeLanguage} disabled={refreshing} testid="weekly-language" />
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="refresh-suggestions-btn" className="gap-2">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> {t("Nye forslag")}
          </Button>
        </div>
      </div>

      {suggestions === null ? (
        <Spinner label="Henter jeres ugentlige forslag..." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="suggestions-list">
          {suggestions.map((s, i) => {
            const m = MODULE_MAP[s.module] || MODULE_MAP.idea;
            return (
              <Surface key={i} delay={i * 0.06} testid={`suggestion-${i}`} className="flex flex-col">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">
                  <m.icon className="h-3.5 w-3.5" /> {t(m.label)}
                </div>
                <h3 className="font-display text-lg font-medium leading-snug">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">{s.description}</p>
                <Button size="sm" onClick={() => draft(s)} className="mt-4 gap-2 self-start" data-testid={`draft-btn-${i}`}>
                  <PenLine className="h-3.5 w-3.5" /> {t("Lav udkast")}
                </Button>
              </Surface>
            );
          })}
        </div>
      )}
    </div>
  );
}
